// src/components/ProtectedRoute.js
import { useEffect, useReducer, createContext, useContext, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "../../supabaseClient"; // Import Supabase client

const initialAuthState = {
  isAuthenticated: false,
  session: null,
};

const AuthorizationContext = createContext();

const authReducer = (state, action) => {
  switch (action.type) {
    case "LOGIN":
      return {
        ...state,
        isAuthenticated: !!action.session,
        session: action.session,
      };
    case "LOGOUT":
      return {
        ...initialAuthState
      };
    default:
      return state;
  }
}

const AuthorizationProvider = ({ children }) => {
  const [authorization, dispatch] = 
    useReducer(authReducer, initialAuthState);

  return (
    <AuthorizationContext.Provider value={{ authorization, dispatch }}>
      {children}
    </AuthorizationContext.Provider>
  );
};


const ProtectedRoute = ({ children }) => {
  return (
    <AuthorizationProvider>
      <ProtectedRouteComponent>
        {children}
      </ProtectedRouteComponent>
    </AuthorizationProvider>
  );
}

const ProtectedRouteComponent = ({ children }) => {
  const { authorization, dispatch } = useContext(AuthorizationContext);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 1. Initial Check
    const checkAuthStatus = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error || !session) {
          throw new Error("No session found");
        }

        dispatch({ type: "LOGIN", session });
      } catch (err) {
        dispatch({ type: "LOGOUT" });
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();

    // 2. Listen for auth changes (This catches the "Refresh Token Not Found" event)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || event === 'USER_UPDATED' && !session) {
        dispatch({ type: "LOGOUT" });
      } else if (session) {
        dispatch({ type: "LOGIN", session });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [dispatch]);

  useEffect(() => {
  const { data: sub } = supabase.auth.onAuthStateChange(
    (_event, session) => {
      if (!session) {
        window.location.href = "/login";
      }
    }
  );

  return () => sub.subscription.unsubscribe();
}, []);


  if (loading) {
    return <p>Loading...</p>;
  }

  if (!authorization.isAuthenticated) {
    // Use 'replace' to prevent the user from hitting "back" to the protected page
    return <Navigate to="/login" replace />;
  }

  return children;
};

export default ProtectedRoute;
export { AuthorizationContext };
